import * as React from "react"
import * as Yup from "yup"
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material"
import { WithCountdownProps, withCountdown } from "@/hoc/withCountdown"

import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace"
import { RegisterSteps } from "."
import { useFormik } from "formik"
import { useTranslation } from "react-i18next"
import { authVerifyEmail } from '@/redux/auth/services'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

interface IRegisterVerifyEmailProps extends WithCountdownProps {
  changeStep: (step: RegisterSteps) => void
  email: string
}

interface IFormikValues {
  verify_code: string
}

const RegisterVerifyEmail: React.FunctionComponent<
  IRegisterVerifyEmailProps
> = ({ changeStep, count, startCountdown, email }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  React.useEffect(() => {
    startCountdown()
  }, [])

  const { t } = useTranslation(["inputValidations", "common"])

  const { values, isSubmitting, errors, touched, handleSubmit, getFieldProps, setErrors } =
    useFormik<IFormikValues>({
      validationSchema: Yup.object().shape({
        verify_code: Yup.string().required(
          t("inputValidations:this_field_is_required")
        )
      }),
      initialValues: {
        verify_code: ""
      },
      onSubmit: (values, actions) => {
        actions.setSubmitting(true)
        dispatch(authVerifyEmail(values.verify_code, (res) => {
          navigate('/dashboard/projects')
        }, (err) => {
          setErrors({ verify_code: err.message })
        }))
      }
    })

  return (
    <>
      <Box component="form" onSubmit={handleSubmit}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => changeStep(RegisterSteps.Inputs)}>
            <KeyboardBackspaceIcon />
          </IconButton>
          <Typography component="h1" variant="h6" sx={{ mb: 2 }}>
            {t("common:sign_up")}
          </Typography>
        </Stack>
        <Stack direction="column" spacing={2}>
          <Typography component="span" sx={{ display: "block", my: 2 }}>
            {t(
              "common:we’ve_sent_you_a_six-digit_confirmation_code_to_{{email}}._please_enter_it_below_to_confirm_yout_email_address.",
              {
                email
              }
            )}
          </Typography>
          <TextField
            {...getFieldProps("verify_code")}
            type="text"
            label={t("common:verify_code")}
            error={Boolean(touched.verify_code && errors.verify_code)}
            helperText={touched.verify_code && errors.verify_code}
          />
          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            disabled={values.verify_code.length === 0}
          >
            {t("common:submit")}
          </Button>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          <Button
            sx={{
              color: (t) => `${t.palette.primary.main} !important`,
              border: 0,
              fontWeight: "bold",
              opacity: count > 0 ? 0.6 : 1
            }}
            variant="text"
            disabled={count > 0}
          >
            {t("common:send_code_again")}
          </Button>
          <Typography component="span">{count.inTimeFormat()}</Typography>
        </Stack>
      </Box>
    </>
  )
}

export default withCountdown(RegisterVerifyEmail, 120)
